// src/pages/AdminUsers.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import Select from "react-select";
import {
  X, Users, UserPlus, UserCheck, UserMinus, UserCog,
  Truck, User, UserCircle2, Eye, EyeOff, Search, Filter,
  ChevronLeft, ChevronRight, Edit2, Shield, Activity, Circle,
  RefreshCw, Phone, Hash, Briefcase, MapPin
} from "lucide-react";

import AdminLayout from "../components/AdminLayout";
import AdminUserAdd from "./AdminUserAdd";
import trackingWS from "../services/trackingWebSocket";
import { fetchUsers as fetchUsersList } from "../actions/UserActions";
import { fetchDepartments } from "../actions/DepartmentActions";
import { fetchVehicles } from "../actions/VehicleActions";
import { fetchBookings } from "../actions/BookingActions";
import { getAllDepartments } from "../services/departmentService";
import { getCroppedImg } from "../utils/cropImage";
import { defaultUserImg } from "../assets/defaultUser";

// ─────────────────────────────────────────────────────────────────────────────
// AdminUserUpdate Component (merged inline)
// ─────────────────────────────────────────────────────────────────────────────

const AdminUserUpdate = ({ isOpen, onClose, serviceNo, onSuccess }) => {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((s) => s.auth);

  const [formData, setFormData] = useState({
    ServiceNo: "",
    Name: "",
    MobileNo: "",
    role: "E",
    status: "A",
    department_code: "",
    password: "",
    file_path: null,
  });

  const [previewImage, setPreviewImage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);

  const roles = [
    { value: "S", label: "Supervisor" },
    { value: "E", label: "Employee" },
    { value: "A", label: "Admin" },
    { value: "EN", label: "Engineer" },
    { value: "D", label: "Driver" },
  ];

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
  ];

  const mapRole = (r) => {
    if (!r) return "E";
    const v = r.toString().trim().toUpperCase();
    switch (v) {
      case "S": case "SUPERVISOR": return "S";
      case "E": case "EMPLOYEE": return "E";
      case "A": case "ADMIN": return "A";
      case "EN": case "ENGINEER": return "EN";
      case "D": case "DRIVER": return "D";
      default: return "E";
    }
  };

  const mapStatus = (s) => {
    const v = s?.toString().trim().toUpperCase();
    return v === "ACTIVE" || v === "A" ? "A" : "I";
  };

  const getUserImgUrl = (sno) =>
    `https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${sno}`;

  useEffect(() => {
    if (!isOpen || !serviceNo) return;

    const loadDept = async () => {
      try {
        const data = await getAllDepartments();
        setDepartments(data || []);
      } catch (err) {
        console.error("Error loading departments", err);
      }
    };

    const fetchUser = async () => {
      setFetching(true);
      setMessage("");
      try {
        const users = await dispatch(fetchUsersList());
        const user = users.find(
          (u) => u.ServiceNo?.toString().trim() === serviceNo?.toString().trim()
        );
        if (!user) {
          setMessage("❌ User not found.");
        } else {
          setFormData({
            ServiceNo: user.ServiceNo,
            Name: user.Name,
            MobileNo: user.MobileNo,
            role: mapRole(user.role),
            status: mapStatus(user.status),
            department_code: user.department_code || user.department || user.Department || "",
            password: user.password || "",
            file_path: user.file_path || null,
          });
          setPreviewImage(getUserImgUrl(user.ServiceNo) + `&t=${Date.now()}`);
        }
      } catch (err) {
        console.error("User fetch error:", err);
        setMessage("❌ Failed to fetch user.");
      } finally {
        setFetching(false);
      }
    };

    loadDept();
    fetchUser();
  }, [serviceNo, authKey, dispatch, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "MobileNo" && !/^\d*$/.test(value)) return;
    if (name === "Name" && !/^[a-zA-Z\s]*$/.test(value)) return;
    setFormData((p) => ({ ...p, [name]: value }));
    setMessage("");
  };

  const handleDeptChange = (option) => {
    setFormData((p) => ({ ...p, department_code: option ? option.value : "" }));
    setMessage("");
  };

  const departmentOptions = departments.map((d) => ({
    value: d.code || d.name || "",
    label: d.name || d.code || "",
  }));

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessage("❌ Please select an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(file);
      setPreviewImage(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const applyCrop = useCallback(async () => {
    if (!croppedAreaPixels || !previewImage) return;
    try {
      const blob = await getCroppedImg(previewImage, croppedAreaPixels);
      setProfileImageBlob(blob);
      setPreviewImage(URL.createObjectURL(blob));
      setShowCropModal(false);
      setSelectedFile(null);
      setMessage("");
    } catch (err) {
      console.error("applyCrop error:", err);
      setMessage("❌ Failed to crop image.");
    }
  }, [croppedAreaPixels, previewImage]);

  const cancelCrop = () => {
    if (formData.ServiceNo) {
      setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
    } else {
      setPreviewImage("");
    }
    setSelectedFile(null);
    setProfileImageBlob(null);
    setShowCropModal(false);
    setMessage("");
  };

  const uploadImageToServer = async (serviceNoLocal, blobOrFile) => {
    const file =
      blobOrFile instanceof File
        ? blobOrFile
        : new File([blobOrFile], `${serviceNoLocal}.jpg`, { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("ServiceNo", serviceNoLocal);
    fd.append("ImageFile", file);
    return axios.post(
      "https://aeroviewbackend.dockyardsoftware.com/User/Fileupload",
      fd,
      { headers: { "Content-Type": "multipart/form-data", "auth-key": authKey || "" } }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!/^\d{10}$/.test(formData.MobileNo)) {
      setMessage("❌ Mobile number must be 10 digits.");
      setLoading(false);
      return;
    }

    try {
      const vehicles = await dispatch(fetchVehicles());
      const assignedVehicle = vehicles.find(
        (v) => v.driverSno?.toString().trim() === formData.ServiceNo?.toString().trim()
      );
      if (assignedVehicle) {
        setMessage(
          `❌ Cannot update: Driver is assigned to vehicle ${assignedVehicle.vehicleId}. Please unassign first.`
        );
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Vehicle check error:", err);
      setMessage("❌ Failed to verify driver assignment status.");
      setLoading(false);
      return;
    }

    try {
      const allBookings = await dispatch(fetchBookings());
      const userBookings = allBookings.filter(
        (b) => b.bookingSno?.toString().trim() === formData.ServiceNo?.toString().trim()
      );
      const activeBooking = userBookings.find((b) => {
        const s = b.status?.toString().trim().toUpperCase();
        if (!s) return false;
        const allowedStatuses = ["C", "CANCEL", "CO", "COMPLETED"];
        return !allowedStatuses.includes(s);
      });
      if (activeBooking) {
        setMessage(
          `❌ Cannot update: User has an active booking (ID: ${activeBooking.bId}, Status: ${activeBooking.status}). Please wait until it is completed.`
        );
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Booking check error:", err);
      setMessage("❌ Failed to verify booking status.");
      setLoading(false);
      return;
    }

    try {
      const userData = {
        ServiceNo: formData.ServiceNo,
        Name: formData.Name,
        MobileNo: formData.MobileNo,
        role: formData.role,
        status: formData.status,
        password: formData.password,
        created_by: "Admin",
        updated_by: "Admin",
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        department_code: formData.department_code,
      };

      const postResp = await axios.post(
        "https://aeroviewbackend.dockyardsoftware.com/UserRegister/PostUserDetails",
        [userData],
        { headers: { "Content-Type": "application/json", "auth-key": authKey || "" } }
      );

      if (postResp?.data?.statusCode !== 200) {
        throw new Error(postResp?.data?.result || "Failed to update user");
      }

      if (profileImageBlob) {
        await uploadImageToServer(formData.ServiceNo, profileImageBlob);
        setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
        setProfileImageBlob(null);
      }

      setMessage("✅ User updated successfully.");
      if (onSuccess) onSuccess();
      setTimeout(() => {
        setMessage("");
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Submit error:", err);
      setMessage("❌ Network or API error.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isCurrentUser =
    userDetails?.ServiceNo?.toString() === formData.ServiceNo?.toString();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 transition-opacity duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 rounded-xl">
              <UserCircle2 className="text-blue-600" size={22} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">Update User</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {message && (
          <div className={`mx-6 mt-4 p-3 rounded-xl text-sm ${message.includes("✅")
            ? "bg-green-50 text-green-700 border border-green-100"
            : "bg-red-50 text-red-700 border border-red-100"
          }`}>
            {message}
          </div>
        )}

        {fetching ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={24} className="text-gray-400 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="flex flex-col items-center gap-3">
              <div className="relative group">
                <img
                  src={previewImage || defaultUserImg}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                  alt="profile"
                  onError={(e) => { e.currentTarget.src = defaultUserImg; }}
                />
                <div
                  className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="text-white text-xs font-medium">Change</span>
                </div>
                {profileImageBlob && (
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProfileImageBlob(null);
                      setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
                    }}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Upload Photo
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Service No</label>
              <div className="relative">
                <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="ServiceNo"
                  value={formData.ServiceNo}
                  readOnly
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile No</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="MobileNo"
                  value={formData.MobileNo}
                  onChange={handleChange}
                  required
                  maxLength={10}
                  pattern="[0-9]{10}"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  disabled={isCurrentUser}
                  className={`w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition ${isCurrentUser ? "bg-gray-50 cursor-not-allowed text-gray-500" : ""
                    }`}
                >
                  {roles.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isCurrentUser}
                  className={`w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition ${isCurrentUser ? "bg-gray-50 cursor-not-allowed text-gray-500" : ""
                    }`}
                >
                  {statuses.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              <Select
                options={departmentOptions}
                value={
                  departmentOptions.find(
                    (o) =>
                      String(o.value).trim() === String(formData.department_code).trim()
                  ) || null
                }
                onChange={handleDeptChange}
                placeholder="Select department"
                isClearable
                className={isCurrentUser ? "opacity-50 pointer-events-none" : ""}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.75rem",
                    borderColor: "#e5e7eb",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#9ca3af" },
                  }),
                }}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current"
                  className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl shadow-sm hover:bg-blue-700 text-sm font-medium transition-all transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? "Updating..." : "Update User"}
              </button>
            </div>
          </form>
        )}
      </div>

      {showCropModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
            <div className="relative h-64 bg-gray-900">
              <Cropper
                image={previewImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="horizontal-cover"
              />
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-2">Zoom</p>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, v) => setZoom(v)}
                  size="small"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={cancelCrop}
                  className="flex-1 px-3 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowCropModal(false);
                    setSelectedFile(null);
                    setTimeout(() => fileInputRef.current?.click?.(), 120);
                  }}
                  className="flex-1 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors"
                  type="button"
                >
                  Change
                </button>
                <button
                  onClick={applyCrop}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AdminUsers Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AdminUsers = () => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedServiceNo, setSelectedServiceNo] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const dispatch = useDispatch();
  const { authKey } = useSelector((state) => state.auth);
  const { items: users, loading, error } = useSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAvailability, setFilterAvailability] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchUsers = useCallback(async () => {
    await dispatch(fetchUsersList());
  }, [dispatch]);

  useEffect(() => {
    if (authKey) fetchUsers();
  }, [authKey, fetchUsers]);

  const { items: departments } = useSelector((state) => state.department || { items: [] });
  useEffect(() => {
    if (authKey) dispatch(fetchDepartments());
  }, [authKey, dispatch]);

  useEffect(() => {
    if (!authKey) return;
    const handleStatusUpdate = (data) => {
      if (data.driverSno) {
        setOnlineUsers((prev) => ({ ...prev, [data.driverSno]: data.isOnline }));
      }
    };
    trackingWS.connect(authKey);
    const unsubscribe = trackingWS.subscribe("ALL", handleStatusUpdate);
    return () => { unsubscribe(); };
  }, [authKey]);

  const handleAddUser = () => setIsAddUserModalOpen(true);
  const handleUpdateUser = (serviceNo) => {
    setSelectedServiceNo(serviceNo);
    setIsUpdateModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilterRole("");
    setFilterDepartment("");
    setFilterStatus("");
    setFilterAvailability("");
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const s = String(search || "").trim().toLowerCase();
      const serviceNo = String(u.ServiceNo ?? "").trim().toLowerCase();
      const name = String(u.Name ?? "").trim().toLowerCase();
      const roleValue = String(u.role ?? "").trim().toLowerCase();
      const deptValue = String(u.department_Name ?? u.Department ?? u.department ?? "").trim().toLowerCase();
      const statusValue = String(u.status ?? "").trim().toLowerCase();

      const matchSearch = !s || serviceNo.includes(s) || name.includes(s);
      const matchRole = !filterRole || roleValue === filterRole.toLowerCase();
      const matchDept = !filterDepartment || deptValue === filterDepartment.toLowerCase();
      const matchStatus = !filterStatus || statusValue === filterStatus.toLowerCase();

      let matchAvailability = true;
      if (filterAvailability && filterRole === "Driver") {
        const isOnline = !!onlineUsers[u.ServiceNo];
        matchAvailability = filterAvailability === "Online" ? isOnline : !isOnline;
      }

      return matchSearch && matchRole && matchDept && matchStatus && matchAvailability;
    });
  }, [users, search, filterRole, filterDepartment, filterStatus, filterAvailability, onlineUsers]);

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  const totalCount = users.length;
  const activeCount = users.filter((u) => u.status === "Active").length;
  const inactiveCount = users.filter((u) => u.status === "Inactive").length;
  const driverUsers = users.filter(u => u.role === "Driver");
  const onlineDriverCount = driverUsers.filter(u => onlineUsers[u.ServiceNo]).length;
  const offlineDriverCount = driverUsers.length - onlineDriverCount;

  const roleCounts = useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      counts[u.role] = (counts[u.role] || 0) + 1;
    });
    return counts;
  }, [users]);

  const roleMeta = {
    Admin: { color: "bg-purple-100 text-purple-800", icon: <Shield size={18} />, borderColor: "border-purple-200" },
    Supervisor: { color: "bg-blue-100 text-blue-800", icon: <UserCheck size={18} />, borderColor: "border-blue-200" },
    Engineer: { color: "bg-amber-100 text-amber-800", icon: <Activity size={18} />, borderColor: "border-amber-200" },
    Driver: { color: "bg-emerald-100 text-emerald-800", icon: <Truck size={18} />, borderColor: "border-emerald-200" },
    Employee: { color: "bg-gray-100 text-gray-800", icon: <UserMinus size={18} />, borderColor: "border-gray-200" },
  };

  const statusColor = (status) => {
    return status === "Active"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-gray-100 text-gray-600 border-gray-200";
  };

  const statsConfig = {
    totalUsers: { bg: "bg-purple-50", iconBg: "bg-purple-100", iconColor: "text-purple-600", label: "Total Users" },
    activeUsers: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", iconColor: "text-emerald-600", label: "Active Users" },
    inactiveUsers: { bg: "bg-gray-50", iconBg: "bg-gray-100", iconColor: "text-gray-600", label: "Inactive Users" },
    driversOnline: { bg: "bg-green-50", iconBg: "bg-green-100", iconColor: "text-green-600", label: "Drivers Online" },
    driversOffline: { bg: "bg-gray-50", iconBg: "bg-gray-100", iconColor: "text-gray-500", label: "Drivers Offline" },
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Users size={24} className="text-gray-700" />
              User Management
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Manage system users, roles, and permissions
            </p>
          </div>
          <button
            onClick={handleAddUser}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <UserPlus size={16} />
            Add User
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className={`${statsConfig.totalUsers.bg} border border-purple-200 rounded-lg p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`${statsConfig.totalUsers.iconBg} p-1.5 rounded-lg`}>
              <Users size={16} className={statsConfig.totalUsers.iconColor} />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{totalCount}</p>
          <p className="text-xs text-gray-600 mt-0.5 font-medium">Total Users</p>
        </div>

        <div className={`${statsConfig.activeUsers.bg} border border-emerald-200 rounded-lg p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`${statsConfig.activeUsers.iconBg} p-1.5 rounded-lg`}>
              <UserCheck size={16} className={statsConfig.activeUsers.iconColor} />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{activeCount}</p>
          <p className="text-xs text-gray-600 mt-0.5 font-medium">Active Users</p>
        </div>

        <div className={`${statsConfig.inactiveUsers.bg} border border-gray-200 rounded-lg p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`${statsConfig.inactiveUsers.iconBg} p-1.5 rounded-lg`}>
              <UserMinus size={16} className={statsConfig.inactiveUsers.iconColor} />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{inactiveCount}</p>
          <p className="text-xs text-gray-600 mt-0.5 font-medium">Inactive Users</p>
        </div>

        <div className={`${statsConfig.driversOnline.bg} border border-green-200 rounded-lg p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`${statsConfig.driversOnline.iconBg} p-1.5 rounded-lg`}>
              <Circle size={16} className="text-green-600 fill-green-500" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{onlineDriverCount}</p>
          <p className="text-xs text-gray-600 mt-0.5 font-medium">Drivers Online</p>
        </div>

        <div className={`${statsConfig.driversOffline.bg} border border-gray-200 rounded-lg p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`${statsConfig.driversOffline.iconBg} p-1.5 rounded-lg`}>
              <Circle size={16} className="text-gray-400" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{offlineDriverCount}</p>
          <p className="text-xs text-gray-600 mt-0.5 font-medium">Drivers Offline</p>
        </div>
      </div>

      {/* Role Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 sm:mb-8">
        {Object.entries(roleMeta).map(([role, meta]) => (
          <div
            key={role}
            className={`bg-white border ${meta.borderColor} rounded-lg p-3 sm:p-4 hover:shadow-sm transition`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={meta.color.replace("text", "bg").replace("800", "100") + " p-1.5 rounded-lg"}>
                {meta.icon}
              </div>
            </div>
            <p className="text-lg sm:text-xl font-semibold text-gray-900">{roleCounts[role] || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">{role}</p>
          </div>
        ))}
      </div>

      {/* Filters Section */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm mb-6">
        <div className="p-4 sm:p-5 border-b border-gray-100">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <Filter size={16} />
            Filters
            <ChevronLeft size={14} className={`transform transition-transform ${showFilters ? "-rotate-90" : "rotate-0"}`} />
          </button>
        </div>

        {showFilters && (
          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID or Name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                />
                {search && (
                  <X size={14} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600" onClick={() => setSearch("")} />
                )}
              </div>

              <select
                value={filterRole}
                onChange={(e) => {
                  setFilterRole(e.target.value);
                  if (e.target.value !== "Driver") setFilterAvailability("");
                }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              >
                <option value="">All Roles</option>
                {Object.keys(roleMeta).map(role => <option key={role} value={role}>{role}</option>)}
              </select>

              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              >
                <option value="">All Departments</option>
                {(departments || []).map((d) => (
                  <option key={d.name || d.code} value={(d.name || d.code || "").toLowerCase()}>
                    {d.name || d.code}
                  </option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {filterRole === "Driver" && (
                <select
                  value={filterAvailability}
                  onChange={(e) => setFilterAvailability(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                >
                  <option value="">All Availability</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
              )}
            </div>

            {(search || filterRole || filterStatus || filterDepartment || filterAvailability) && (
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Showing {filteredUsers.length} of {users.length} users
                </p>
                <button
                  onClick={handleClearFilters}
                  className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <X size={12} />
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="bg-white border border-gray-100 rounded-lg p-8 text-center">
          <RefreshCw size={24} className="mx-auto text-gray-300 animate-spin" />
          <p className="text-sm text-gray-500 mt-3">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentUsers.map((user) => (
                    <tr key={user.ServiceNo} className="hover:bg-gray-50/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={`https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${user.ServiceNo}`}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover border border-gray-200"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.Name || "U")}&background=9CA3AF&color=fff&size=32`;
                            }}
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-gray-900">{user.Name}</span>
                            {user.role === "Driver" && (
                              <span
                                className={`w-2 h-2 rounded-full ${onlineUsers[user.ServiceNo] ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`}
                                title={onlineUsers[user.ServiceNo] ? "Online" : "Offline"}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{user.ServiceNo}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.MobileNo}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded ${roleMeta[user.role]?.color || "bg-gray-100 text-gray-800"}`}>
                          {roleMeta[user.role]?.icon}
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {user.department_Name || user.Department || user.department || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${statusColor(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleUpdateUser(user.ServiceNo)}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                        >
                          <Edit2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {currentUsers.map((user) => (
              <div key={user.ServiceNo} className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={`https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${user.ServiceNo}`}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.Name || "U")}&background=9CA3AF&color=fff&size=40`;
                      }}
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold text-gray-900">{user.Name}</p>
                        {user.role === "Driver" && (
                          <span
                            className={`w-2 h-2 rounded-full ${onlineUsers[user.ServiceNo] ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`}
                          />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{user.ServiceNo}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateUser(user.ServiceNo)}
                    className="p-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>

                <div className="space-y-2 pt-2 border-t border-gray-50">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Mobile</span>
                    <span className="font-medium text-gray-700">{user.MobileNo}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Role</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${roleMeta[user.role]?.color || "bg-gray-100 text-gray-800"}`}>
                      {roleMeta[user.role]?.icon}
                      {user.role}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Department</span>
                    <span className="text-gray-700">{user.department_Name || user.Department || user.department || "—"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Status</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${statusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredUsers.length === 0 && !loading && (
            <div className="bg-white border border-gray-100 rounded-lg p-12 text-center">
              <Users size={48} className="mx-auto text-gray-300" />
              <p className="text-sm text-gray-500 mt-3">No users found</p>
              <button
                onClick={handleClearFilters}
                className="mt-3 text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-xs text-gray-500">
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredUsers.length)} of {filteredUsers.length}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  <ChevronLeft size={16} className="text-gray-600" />
                </button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  <ChevronRight size={16} className="text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <AdminUserAdd
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={fetchUsers}
      />

      <AdminUserUpdate
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        serviceNo={selectedServiceNo}
        onSuccess={fetchUsers}
      />
    </AdminLayout>
  );
};

export default AdminUsers;